import { Injectable } from '@angular/core';
import { LngLatLike } from '@trimblemaps/trimblemaps-js';
import { has } from 'lodash-es';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { SingleSearchLocation } from '../_models/pcmws/singleSearch/singleSearchResponse';
import { StopLocation } from '../_models/state/stopState';
import { PlaceSetService } from './place-set.service';
import { SearchService } from './search.service';
import { RegionCode } from '../utils/regions';
import { UserService } from './user.service';

interface AddStopMarker {
  location: StopLocation;
  canZoom: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AddStopMarkerService {
  private location = new BehaviorSubject<AddStopMarker>({
    location: null,
    canZoom: false,
  });
  readonly location$ = this.location.asObservable();

  private isLoading = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading.asObservable();

  private lngLat = new Subject<LngLatLike>();
  readonly lngLat$ = this.lngLat.asObservable();

  private error = new BehaviorSubject<string>(null);
  readonly error$ = this.error.asObservable();

  constructor(
    private searchService: SearchService,
    private placeSetService: PlaceSetService,
    private userService: UserService
  ) {}

  addMarker(stopPoint: LngLatLike | StopLocation) {
    if (has(stopPoint, 'address')) {
      this.addMarkerUsingStop(stopPoint as StopLocation);
    } else {
      this.addMarkerUsingLngLat(stopPoint as LngLatLike);
    }
  }

  private addMarkerUsingStop(stop: StopLocation) {
    this.lngLat.next(stop?.coords);
    this.isLoading.next(true);
    this.location.next({
      location: stop,
      canZoom: true,
    });
    this.isLoading.next(false);
    this.error.next(null);
  }

  addMarkerUsingLngLat(lngLat: LngLatLike): Observable<StopLocation> {
    return this.searchService.reverseGeocode(lngLat).pipe(
      switchMap((location: SingleSearchLocation) => {
        const user = this.userService.getUser();
        if (RegionCode[location.Region] === user.region) {
          this.location.next({
            ...this.location.getValue(),
            ...{ canZoom: false },
          });
          this.lngLat.next(lngLat);
          this.isLoading.next(true);
          return this.placeSetService.checkPlaceCoordsBelongsToFavorite(
            location
          );
        }
        this.lngLat.next(null);
        return of(null);
      }),
      tap((stop: StopLocation) => {
        if (stop) {
          this.location.next({
            ...this.location.getValue(),
            ...{ location: stop },
          });
        }
        this.isLoading.next(false);
        this.error.next(null);
      }),
      catchError((err) => {
        const loc: StopLocation = {
          coords: [Number.parseFloat(lngLat[0]), Number.parseFloat(lngLat[1])],
        };
        this.lngLat.next(lngLat);
        this.isLoading.next(false);
        this.location.next({
          location: loc,
          canZoom: false,
        });
        this.error.next(err?.error?.ErrString);
        return of(loc);
      })
    );
  }
  updateMarker(location: StopLocation) {
    this.lngLat.next(location?.coords);
    this.location.next({ location: location, canZoom: false });
  }
}
